import React, { useState } from 'react'
import {
    Box, Button, Card, CardHeader, CardContent, Typography,
    Grid, FormControl, FormLabel, OutlinedInput,
    Select, MenuItem, Chip, Divider, Link
} from '@material-ui/core'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import SyncAltIcon from '@material-ui/icons/SyncAlt'
import ProviderAPI from 'config/api/ProviderAPI'
import Loader from 'components/general/Loader'
import { onError } from 'config/lib/errorLib'
import { notifyMilestone } from 'config/helpers/integrations-lib'
import { NotificationManager } from 'react-notifications'
import getLeadPrice from 'config/helpers/leadPricing'
import DeliveryMethodModal from 'components/provider/DeliveryCard'

const CAP_OPTIONS = [5, 10, 15, 20, 25, 30, 40, 50]

// --- date helpers -----------------------------------------------------------

function getUpcomingSaturday(fromDate = new Date()) {
    const d = new Date(fromDate)
    const day = d.getDay()
    const diff = (6 - day + 7) % 7
    d.setDate(d.getDate() + diff)
    d.setHours(0, 0, 0, 0)
    return d
}

function getCurrentWeekSaturday(fromDate = new Date()) {
    const d = new Date(fromDate)
    const day = d.getDay()
    const diff = (day - 6 + 7) % 7
    d.setDate(d.getDate() - diff)
    d.setHours(0, 0, 0, 0)
    return d
}

function getWeekEnd(startSat) {
    const d = new Date(startSat)
    d.setDate(d.getDate() + 6)
    d.setHours(23, 59, 59, 999)
    return d
}

function getBillingMonday(forFriday) {
    const d = new Date(forFriday)
    do { d.setDate(d.getDate() + 1) } while (d.getDay() !== 1)
    d.setHours(9, 0, 0, 0)
    return d
}

function fmtDate(d) {
    return d.toLocaleDateString('en-IE', { day: 'numeric', month: 'short' })
}

// --- tile -------------------------------------------------------------------

function StatTile({ label, value, muted, children }) {
    return (
        <Box
            p={2}
            style={{
                borderRadius: 8,
                backgroundColor: '#f7fafc',
                height: '100%',
                border: '1px solid #edf2f7'
            }}
        >
            <Typography variant='caption' style={{ color: '#8898aa', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {label}
            </Typography>
            {value !== undefined && (
                <Box mt={0.5}>
                    <Typography variant='h4' style={{ color: muted ? '#8898aa' : '#32325d', fontWeight: 600 }}>
                        {value}
                    </Typography>
                </Box>
            )}
            {children}
        </Box>
    )
}

// ---------------------------------------------------------------------------

function BillingDeliveryCard({ companyDetails, paymentMethod, updateCompany }) {
    const api = new ProviderAPI()

    const planDetails = companyDetails.planDetails || {}
    const deliveryDetails = companyDetails.deliveryDetails || {}
    const planType = (companyDetails.planType || 'weekly').toLowerCase()
    const billingPeriod = (companyDetails.billingPeriod || 'weekly').toLowerCase()
    const isTopup = planType === 'topup'

    const currentCap = planDetails.maxLeads || 0
    const isActive = planDetails.pauseRequested === false
    const coverageType = (companyDetails.coverageType || 'counties').toLowerCase()
    const countyCount = (companyDetails.counties || []).length
    const leadPrice = (planDetails.leadPrice != null && planDetails.leadPrice !== '')
        ? Number(planDetails.leadPrice)
        : getLeadPrice(coverageType, countyCount)

    const deliveryMethod = deliveryDetails.deliveryMethod || null
    const deliveryEmail = deliveryDetails.email || null

    // Gating for activation: provider can only resume/activate delivery once a
    // payment method is on file and a delivery method is configured.
    const hasPaymentMethod = !!paymentMethod
    const hasDeliveryMethod = !!deliveryMethod
    const canActivate = hasPaymentMethod && hasDeliveryMethod
    const resumeBlockedReason = !hasPaymentMethod && !hasDeliveryMethod
        ? 'Add a payment method and set up a delivery method to start delivery.'
        : !hasPaymentMethod
            ? 'Add a payment method to start delivery.'
            : !hasDeliveryMethod
                ? 'Set up a delivery method to start delivery.'
                : null

    const now = new Date()
    const weekStart = isActive ? getCurrentWeekSaturday(now) : getUpcomingSaturday(now)
    const weekEnd = getWeekEnd(weekStart)
    const billingMonday = getBillingMonday(weekEnd)

    const [selectedCap, setSelectedCap] = useState(currentCap || CAP_OPTIONS[0])
    const [savingCap, setSavingCap] = useState(false)
    const [togglingPause, setTogglingPause] = useState(false)
    const [showDeliveryModal, setShowDeliveryModal] = useState(false)

    const capOptions = Array.from(new Set([...CAP_OPTIONS, currentCap].filter(Boolean))).sort((a, b) => a - b)

    async function saveCap() {
        if (!selectedCap || selectedCap === currentCap) return
        setSavingCap(true)
        try {
            const updated = await api.updatePlanDetails(companyDetails.companyId, {
                ...planDetails,
                maxLeads: Number(selectedCap)
            })
            updateCompany(updated)
            if (!currentCap || currentCap <= 0) notifyMilestone(companyDetails.name, 'Weekly Cap Set')
            NotificationManager.success('Weekly cap updated. We\'ll apply this as soon as possible — by next delivery week at the latest.')
        } catch (e) {
            onError(e)
        }
        setSavingCap(false)
    }

    async function togglePause() {
        // Prevent resuming/activating delivery until prerequisites are in place.
        if (!isActive && !canActivate) return
        setTogglingPause(true)
        try {
            const updated = await api.setPauseRequest(companyDetails.companyId, isActive)
            updateCompany(updated)
            if (!isActive) notifyMilestone(companyDetails.name, 'Active Delivery Started')
            NotificationManager.success(
                isActive
                    ? 'Stop requested. We\'ll apply this as soon as possible — by next delivery week at the latest.'
                    : 'Start requested. We\'ll apply this as soon as possible — by next delivery week at the latest.'
            )
        } catch (e) {
            onError(e)
        }
        setTogglingPause(false)
    }

    const statusChip = (
        <Chip
            size='small'
            label={isActive ? 'Active' : 'Paused'}
            style={{
                backgroundColor: isActive ? '#229626' : '#fbc02d',
                color: 'white',
                fontWeight: 600
            }}
        />
    )

    return (
        <>
            {showDeliveryModal && (
                <DeliveryMethodModal
                    open={showDeliveryModal}
                    onClose={() => setShowDeliveryModal(false)}
                    companyDetails={companyDetails}
                    updateCompany={updateCompany}
                />
            )}
            <Card>
                <CardHeader
                    subheader={
                        <Box display='flex' alignItems='center' justifyContent='space-between'>
                            <Box component={Typography} variant='h3' marginBottom='0!important'>
                                Delivery Settings
                            </Box>
                            {statusChip}
                        </Box>
                    }
                />
                <CardContent>
                    {/* Top row: quick stats */}
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={4}>
                            <StatTile label='Weekly cap' value={currentCap ? `${currentCap} leads` : '—'} muted={!currentCap} />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <StatTile label='Price per lead' value={`€${leadPrice.toFixed(2)}`} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <StatTile label='Method' muted={!deliveryMethod}>
                                <Box mt={0.5} display='flex' alignItems='center' style={{ gap: 6 }}>
                                    {deliveryMethod === 'Email' && <MailOutlineIcon fontSize='small' style={{ color: '#32325d' }} />}
                                    {deliveryMethod === 'CRM' && <SyncAltIcon fontSize='small' style={{ color: '#32325d' }} />}
                                    <Typography variant='h4' style={{ color: deliveryMethod ? '#32325d' : '#8898aa', fontWeight: 600 }}>
                                        {deliveryMethod || 'Not set'}
                                    </Typography>
                                </Box>
                                {deliveryMethod === 'Email' && deliveryEmail && (
                                    <Typography variant='caption' style={{ display: 'block', color: '#525f7f', wordBreak: 'break-all' }}>
                                        {deliveryEmail}
                                    </Typography>
                                )}
                                <Link
                                    component='button'
                                    variant='caption'
                                    onClick={() => setShowDeliveryModal(true)}
                                    style={{ color: '#229626', marginTop: 4 }}
                                >
                                    {deliveryMethod ? 'Change' : 'Set up'}
                                </Link>
                            </StatTile>
                        </Grid>
                    </Grid>

                    {/* Period info */}
                    <Box mt={3}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormLabel>{isActive ? 'Current delivery week' : 'Next delivery week'}</FormLabel>
                                <Typography variant='body2' style={{ color: '#525f7f' }}>
                                    {fmtDate(weekStart)} – {fmtDate(weekEnd)} (Sat – Fri)
                                </Typography>
                            </Grid>
                            {!isTopup && billingPeriod === 'weekly' && (
                                <Grid item xs={12} sm={6}>
                                    <FormLabel>Next billing date</FormLabel>
                                    <Typography variant='body2' style={{ color: '#525f7f' }}>
                                        {fmtDate(billingMonday)} (weekly in arrears)
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>

                    <Box mt={3}><Divider /></Box>

                    {/* Controls */}
                    <Box mt={3}>
                        <Typography variant='caption' style={{ color: '#8898aa' }}>
                            We'll try to apply changes right away, but depending on where we are in the delivery week they may not take effect until the following week.
                        </Typography>

                        <Grid container spacing={3} style={{ marginTop: 4 }}>
                            <Grid item xs={12} sm={7}>
                                <FormLabel>Weekly cap</FormLabel>
                                <Box display='flex' alignItems='center' style={{ gap: '0.75rem', marginTop: 4 }}>
                                    <FormControl variant='filled' component={Box} minWidth='120px'>
                                        <Select
                                            value={selectedCap}
                                            onChange={(e) => setSelectedCap(e.target.value)}
                                            input={<OutlinedInput />}
                                        >
                                            {capOptions.map(n => (
                                                <MenuItem key={n} value={n}>{n} leads</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        onClick={saveCap}
                                        disabled={savingCap || selectedCap === currentCap}
                                    >
                                        {!savingCap ? 'Update cap' : <Loader height={12} />}
                                    </Button>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <FormLabel>Delivery</FormLabel>
                                <Box mt={0.5}>
                                    <Button
                                        variant='outlined'
                                        color='primary'
                                        onClick={togglePause}
                                        disabled={togglingPause || (!isActive && !canActivate)}
                                        fullWidth
                                    >
                                        {togglingPause
                                            ? <Loader height={12} />
                                            : (isActive ? 'Stop delivery' : 'Start delivery')}
                                    </Button>
                                    {!isActive && resumeBlockedReason && (
                                        <Typography variant='caption' style={{ marginTop: 4, display: 'block', color: '#8898aa' }}>
                                            {resumeBlockedReason}
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </CardContent>
            </Card>
        </>
    )
}

export default BillingDeliveryCard
